import {CustomerProfileStore, Permission, PERMISSION_STATE, SessionStore, UIStore} from '../store';
import {Platform, Loading, Dialog} from 'quasar';
import {PushNotifications, Token, PushNotificationSchema, ActionPerformed} from '@capacitor/push-notifications';
import {AndroidSettings, IOSSettings, NativeSettings} from 'capacitor-native-settings';
import {Component} from 'vue';

class Push {
	carPassComponent: Component|null = null;

	registrationResolve: ((token: string) => void) | null = null;
	registrationReject: ((error: any) => void) | null = null;

	attachPushListeners() {
		if(Platform.is.capacitor) {
			PushNotifications.removeAllListeners().catch(error => {
				console.error(error);
			});

			// On success, we should be able to receive notifications
			PushNotifications.addListener('registration', async (token: Token) => {
				console.log(`Push registration success, token: ${token.value}`);
				await CustomerProfileStore.updatePushNotificationToken({
					token: token.value
				});
				if(this.registrationResolve) {
					console.log('resolving registration');
					this.registrationResolve(token.value);
					this.registrationResolve = null;
				}
			});

			// Some issue with our setup and push will not work
			PushNotifications.addListener('registrationError', (error: any) => {
				console.log('Error on registration: ' + JSON.stringify(error));
				if(this.registrationReject) {
					console.log('rejecting registration');
					this.registrationReject(error);
					this.registrationReject = null;
				}
			});

			// Show us the notification payload if the app is open on our device
			PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
				console.log('Push received: ' + JSON.stringify(notification));
				this.handlePushNotification(notification.data ?? {});
			});

			// Method called when tapping on a notification
			PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
				console.log('Push action performed: ' + JSON.stringify(notification));
				this.handlePushNotification(notification.notification.data ?? {});
			});
		}
	}

	handlePushNotification(data: any) {
		if(data.type) {
			switch(data.type) {
				case 'login/car-pass':
					if(this.carPassComponent != null) {
						console.log(data, data.locationBase, data.qrCode, data.time);
						Dialog.create({
							component: this.carPassComponent,
							locationBase: data.locationBase,
							code: data.qrCode,
							timestamp: data.time
						});
					}
					break;
				default:
					console.log(`unknown message type detected for push notification: ${data.type}`);
			}
		}
	}

	/**
	 * This is called on app start to make sure we always have the most up-to-date token
	 */
	async startupRegister() {
		const state = await this.getPermissionState();
		if(state === PERMISSION_STATE.GRANTED) {
			await PushNotifications.register();
		} else {
			// If we got here it means somehow we lost permission to notifications
			// but the web still thinks we can do it, let's fix the web
			await this.disablePushNotifications();
		}
	}


	async enablePushNotifications() {
		const state = await this.getPermissionState();

		if(state === PERMISSION_STATE.GRANTED) {
			// If it's already granted we can just re-register
			const promise = new Promise((resolve, reject) => {
				this.registrationResolve = resolve;
				this.registrationReject = reject;
			});
			await PushNotifications.register();
			return promise;
		} else if(state === PERMISSION_STATE.PROMPT) {
			// We've never asked the user for permission (system), so we will pop our own request first
			UIStore.setShowPushNotificationRequest(true);
		} else if(state === PERMISSION_STATE.DENIED) {
			// We've asked the user for permission, but they have denied it
			// We will redirect them to our help page
			UIStore.setShowPushNotificationDisabled(true);
		}
	}

	async getPermissionState() {
		const result = await PushNotifications.checkPermissions();
		SessionStore.setPermissionState({
			name: Permission.PUSH_NOTIFICATIONS,
			state: result.receive
		});
		return result.receive;
	}

	async disablePushNotifications() {
		return CustomerProfileStore.updatePushNotificationToken({
			token: null
		});
	}

	async requestPermission() {
		const result = await PushNotifications.requestPermissions();
		if(result.receive === PERMISSION_STATE.GRANTED) {
			// Register with Apple / Google to receive push via APNS/FCM
			await PushNotifications.register();
			return true;
		} else {
			UIStore.setShowPushNotificationDisabled(true);
			return false;
		}
	}

	async openPushNotificationSettings() {
		Loading.show();
		try {
			await NativeSettings.open({
				optionIOS: IOSSettings.App,
				optionAndroid: AndroidSettings.Location,
			});
		} catch(error) {
			console.error(error);
		}
		Loading.hide();
	}

	registerCarPassComponent(component: Component) {
		this.carPassComponent = component;
	}
}



export const PushService = new Push();
